import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography
} from 'antd';
import { EyeOutlined, ReloadOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import Breadcrumb from '@components/Breadcrumb';
import ContainerBody from '@components/ContainerBody';
import Page403 from '@page/Common/403/403';
import { showError, showSuccess } from '@components/notification';
import { usePermission } from '@contexts/Auth/UsePermission';
import {
  apiGetPartnerDailyDeliveries,
  apiGetPartnerDailyDeliveryBatches,
  apiRunPartnerDailyDelivery
} from '@services/PartnerDailyDelivery/PartnerDailyDeliveryService';

const statusMeta = {
  RUNNING: { color: 'processing', label: 'Đang gửi' },
  SUCCESS: { color: 'success', label: 'Thành công' },
  PARTIAL: { color: 'warning', label: 'Một phần' },
  FAILED: { color: 'error', label: 'Thất bại' },
  SENDING: { color: 'processing', label: 'Đang gửi' }
};

const StatusTag = ({ status }) => {
  const meta = statusMeta[status] || { color: 'default', label: status || '-' };
  return <Tag color={meta.color}>{meta.label}</Tag>;
};

const DetailMetric = ({ title, value, color }) => (
  <div style={{ padding: '10px 14px', borderRadius: 8, background: '#f5f7fa' }}>
    <Typography.Text type="secondary">{title}</Typography.Text>
    <div style={{ marginTop: 2, fontSize: 20, fontWeight: 600, color: color || '#1f2937' }}>
      {Number(value || 0).toLocaleString('vi-VN')}
    </div>
  </div>
);

const PartnerDailyDelivery = () => {
  const { isSuperAdmin } = usePermission();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [runLoading, setRunLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState(undefined);
  const [deliveryType, setDeliveryType] = useState('DAILY');
  const [targetDate, setTargetDate] = useState(dayjs().subtract(1, 'day'));
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiGetPartnerDailyDeliveries({
        page,
        size: pageSize,
        status,
        delivery_type: deliveryType
      });
      setData((response.data?.data || []).map((item) => ({ ...item, key: item.id })));
      setTotal(response.data?.meta?.total || 0);
    } catch (error) {
      setData([]);
      showError(error?.message || 'Không thể tải nhật ký gửi dữ liệu đối tác.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) fetchData();
  }, [isSuperAdmin, page, pageSize, status, deliveryType]);

  useEffect(() => {
    if (!data.some((item) => item.status === 'RUNNING')) return undefined;
    const timer = setInterval(fetchData, 5000);
    return () => clearInterval(timer);
  }, [data]);

  const startRun = async () => {
    try {
      setRunLoading(true);
      await apiRunPartnerDailyDelivery(targetDate.format('YYYY-MM-DD'), deliveryType);
      showSuccess({ message: 'Đã bắt đầu gửi dữ liệu sang đối tác.' });
      setPage(1);
      await fetchData();
    } catch (error) {
      showError(error?.message || 'Không thể bắt đầu gửi dữ liệu.');
    } finally {
      setRunLoading(false);
    }
  };

  const openDetail = async (id) => {
    try {
      setDetailLoading(true);
      setDetail({ open: true, run: null, batches: [] });
      const response = await apiGetPartnerDailyDeliveryBatches(id);
      setDetail({ open: true, ...(response.data?.data || {}) });
    } catch (error) {
      setDetail(null);
      showError(error?.message || 'Không thể tải chi tiết lần gửi.');
    } finally {
      setDetailLoading(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <ContainerBody>
        <Page403 />
      </ContainerBody>
    );
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    {
      title: 'Loại dữ liệu',
      dataIndex: 'delivery_type',
      width: 110,
      render: (value) => (value === 'HOURLY' ? 'Theo giờ' : 'Theo ngày')
    },
    {
      title: 'Ngày dữ liệu',
      dataIndex: 'target_date',
      width: 125,
      render: (value) => dayjs(value).format('DD/MM/YYYY')
    },
    {
      title: 'Nguồn chạy',
      dataIndex: 'trigger_type',
      width: 110,
      render: (value) => (value === 'SCHEDULED' ? 'Tự động' : 'Thủ công')
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 115,
      render: (value) => <StatusTag status={value} />
    },
    { title: 'Tổng dữ liệu', dataIndex: 'total_records', width: 105, align: 'right' },
    { title: 'Đã gửi', dataIndex: 'sent_records', width: 90, align: 'right' },
    { title: 'Thành công', dataIndex: 'success_records', width: 105, align: 'right' },
    { title: 'Lỗi', dataIndex: 'failed_records', width: 75, align: 'right' },
    { title: 'Số lô', dataIndex: 'total_batches', width: 75, align: 'right' },
    {
      title: 'Bắt đầu',
      dataIndex: 'started_at',
      width: 160,
      render: (value) => dayjs(value).format('DD/MM/YYYY HH:mm:ss')
    },
    {
      title: 'Kết thúc',
      dataIndex: 'completed_at',
      width: 160,
      render: (value) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '-')
    },
    { title: 'Người chạy', dataIndex: 'created_by', width: 130 },
    {
      title: '',
      key: 'action',
      width: 60,
      fixed: 'right',
      render: (_, record) => (
        <Button type="text" icon={<EyeOutlined />} onClick={() => openDetail(record.id)} />
      )
    }
  ];

  const batchColumns = [
    { title: 'Lô', dataIndex: 'batch_number', width: 65 },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 110,
      render: (value) => <StatusTag status={value} />
    },
    { title: 'Số bản ghi', dataIndex: 'record_count', width: 105, align: 'right' },
    { title: 'Thành công', dataIndex: 'success_count', width: 105, align: 'right' },
    { title: 'Lỗi', dataIndex: 'failed_count', width: 75, align: 'right' },
    { title: 'Thông báo lỗi', dataIndex: 'error_message', ellipsis: { showTitle: true } }
  ];

  const latest = data[0] || {};

  return (
    <ContainerBody>
      <div className="breadcrumb">
        <Breadcrumb
          homePath="/dashboard"
          routeSegments={[{ name: 'Quản lý dữ liệu gửi đối tác' }]}
        />
      </div>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card>
            <Statistic title="Tổng dữ liệu lần gần nhất" value={latest.total_records || 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic title="Đã gửi" value={latest.sent_records || 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Thành công"
              value={latest.success_records || 0}
              valueStyle={{ color: '#059669' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Lỗi"
              value={latest.failed_records || 0}
              valueStyle={{ color: '#e11d48' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Row justify="space-between" align="middle" gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Nhật ký gửi tiêu thụ đối tác
            </Typography.Title>
          </Col>
          <Col>
            <Space wrap>
              <Select
                value={deliveryType}
                style={{ width: 130 }}
                options={[
                  { value: 'DAILY', label: 'Theo ngày' },
                  { value: 'HOURLY', label: 'Theo giờ' }
                ]}
                onChange={(value) => {
                  setDeliveryType(value);
                  setPage(1);
                }}
              />
              <Select
                allowClear
                placeholder="Trạng thái"
                value={status}
                style={{ width: 140 }}
                options={['RUNNING', 'SUCCESS', 'PARTIAL', 'FAILED'].map((value) => ({
                  value,
                  label: statusMeta[value].label
                }))}
                onChange={(value) => {
                  setStatus(value);
                  setPage(1);
                }}
              />
              <DatePicker
                value={targetDate}
                format="DD/MM/YYYY"
                allowClear={false}
                disabledDate={(date) => date && date.isAfter(dayjs(), 'day')}
                onChange={setTargetDate}
              />
              <Button icon={<ReloadOutlined />} onClick={fetchData}>
                Làm mới
              </Button>
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={runLoading}
                onClick={startRun}>
                Gửi dữ liệu
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          bordered
          loading={loading}
          columns={columns}
          dataSource={data}
          scroll={{ x: 1400 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (value) => `Tổng ${value} lần gửi`,
            onChange: (nextPage, nextSize) => {
              setPage(nextSize !== pageSize ? 1 : nextPage);
              setPageSize(nextSize);
            }
          }}
        />
      </Card>

      <Modal
        title="Chi tiết lần gửi"
        open={Boolean(detail?.open)}
        width={960}
        footer={null}
        style={{ top: 24 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 135px)', overflowY: 'auto', padding: '16px 20px' }}
        onCancel={() => setDetail(null)}>
        {detail?.run && (
          <>
            <Row gutter={[12, 8]} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={6}>
                <Typography.Text type="secondary">Ngày dữ liệu</Typography.Text>
                <div>{dayjs(detail.run.target_date).format('DD/MM/YYYY')}</div>
              </Col>
              <Col xs={12} sm={6}>
                <Typography.Text type="secondary">Loại dữ liệu</Typography.Text>
                <div>{detail.run.delivery_type === 'HOURLY' ? 'Theo giờ' : 'Theo ngày'}</div>
              </Col>
              <Col xs={12} sm={6}>
                <Typography.Text type="secondary">Trạng thái</Typography.Text>
                <div>
                  <StatusTag status={detail.run.status} />
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <Typography.Text type="secondary">Nguồn chạy</Typography.Text>
                <div>{detail.run.trigger_type === 'SCHEDULED' ? 'Tự động' : 'Thủ công'}</div>
              </Col>
            </Row>

            <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={6}>
                <DetailMetric title="Tổng dữ liệu" value={detail.run.total_records} />
              </Col>
              <Col xs={12} sm={6}>
                <DetailMetric title="Đã gửi" value={detail.run.sent_records} />
              </Col>
              <Col xs={12} sm={6}>
                <DetailMetric
                  title="Thành công"
                  value={detail.run.success_records}
                  color="#059669"
                />
              </Col>
              <Col xs={12} sm={6}>
                <DetailMetric title="Lỗi" value={detail.run.failed_records} color="#e11d48" />
              </Col>
            </Row>

            {detail.run.error_message && (
              <details
                style={{
                  marginBottom: 16,
                  padding: '10px 12px',
                  borderRadius: 8,
                  background: '#fff2f0',
                  border: '1px solid #ffccc7'
                }}>
                <summary style={{ cursor: 'pointer', color: '#a8071a', wordBreak: 'break-word' }}>
                  <strong>Chi tiết lỗi</strong> — {detail.run.error_message.slice(0, 140)}
                  {detail.run.error_message.length > 140 ? '…' : ''}
                </summary>
                <div
                  style={{
                    maxHeight: 160,
                    overflowY: 'auto',
                    marginTop: 8,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                  {detail.run.error_message}
                </div>
              </details>
            )}
          </>
        )}
        <Typography.Title level={5} style={{ margin: '0 0 12px' }}>
          Các lô đã gửi ({detail?.batches?.length || 0})
        </Typography.Title>
        <Table
          bordered
          size="small"
          loading={detailLoading}
          columns={batchColumns}
          dataSource={(detail?.batches || []).map((item) => ({ ...item, key: item.id }))}
          scroll={{ x: 750, y: 300 }}
          pagination={{ pageSize: 8, showSizeChanger: false, size: 'small' }}
          expandable={{
            expandedRowRender: (record) => (
              <pre
                style={{
                  maxHeight: 220,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                {JSON.stringify(
                  { request: record.request_payload, response: record.response_body },
                  null,
                  2
                )}
              </pre>
            )
          }}
        />
      </Modal>
    </ContainerBody>
  );
};

export default PartnerDailyDelivery;
